import type { Section } from '@/api/access'
import { useIsAdmin } from '@/components/auth/AdminGuard'
import { TeamSwitcher } from '@/components/layout/TeamSwitcher'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { HELP_URL } from '@/config'
import { useBalance, useOpenTicketCount } from '@/hooks'
import { useTeamStore } from '@/store/team'
import {
  BarChart3,
  CreditCard,
  FileText,
  Ghost,
  Gift,
  Globe,
  HelpCircle,
  LayoutDashboard,
  LineChart,
  Mail,
  Megaphone,
  MessageSquare,
  Plug,
  Radio,
  Route,
  Settings,
  Shield,
  Users,
} from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'

interface NavItem {
  title: string
  icon: React.ElementType
  href: string
  /** Section this nav item maps to for access filtering. Undefined = always visible. */
  section?: Section
  external?: boolean
}

const mainNavItems: NavItem[] = [
  { title: 'Dashboard', icon: LayoutDashboard, href: '/' },
  { title: 'Analytics', icon: LineChart, href: '/analytics' },
  { title: 'Campaigns', icon: Megaphone, href: '/campaigns', section: 'campaigns' },
  { title: 'Offers', icon: Gift, href: '/offers', section: 'offers' },
  { title: 'Sources', icon: Radio, href: '/sources', section: 'sources' },
  { title: 'Domains', icon: Globe, href: '/domains', section: 'domains' },
  { title: 'Sites', icon: FileText, href: '/sites', section: 'landings' },
  { title: 'Site Routes', icon: Route, href: '/site-routes', section: 'landings' },
  { title: 'Integrations', icon: Plug, href: '/integrations', section: 'networks' },
  { title: 'Reports', icon: BarChart3, href: '/reports', section: 'reports' },


]

const bottomNavItems: NavItem[] = [
  { title: 'Support', icon: MessageSquare, href: '/support' },
  { title: 'Affiliate', icon: Users, href: '/affiliate', section: 'billing' },
  { title: 'Billing', icon: CreditCard, href: '/billing', section: 'billing' },
  { title: 'Settings', icon: Settings, href: '/settings', section: 'settings' },
  { title: 'Help', icon: HelpCircle, href: HELP_URL, external: true },
]

const adminNavItems: NavItem[] = [
  { title: 'Users', icon: Users, href: '/admin/users' },
  { title: 'Affiliate', icon: Gift, href: '/admin/affiliate' },
  { title: 'Payouts', icon: CreditCard, href: '/admin/payouts' },
  { title: 'Tickets', icon: MessageSquare, href: '/admin/tickets' },
  { title: 'Invites', icon: Mail, href: '/admin/invites' },
]

interface AppSidebarProps {
  onNavigate?: () => void
}

export function AppSidebar({ onNavigate }: AppSidebarProps) {
  const location = useLocation()
  const canAccessSection = useTeamStore((state) => state.canAccessSection)
  const isAdmin = useIsAdmin()
  const { data: balance } = useBalance()
  const openTicketCount = useOpenTicketCount()

  const formatBalance = (cents: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(cents / 100)
  }

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  const handleClick = () => {
    onNavigate?.()
  }

  // Filter nav items based on section access — items without a section are always visible
  const visibleMainItems = mainNavItems.filter(
    (item) => !item.section || canAccessSection(item.section)
  )
  const visibleBottomItems = bottomNavItems.filter(
    (item) => !item.section || canAccessSection(item.section)
  )

  return (
    <Sidebar>
      <SidebarHeader className="border-b">
        <Link to="/" className="flex items-center gap-2 px-6 py-3" onClick={handleClick}>
          <Ghost className="h-7 w-7 text-primary" />
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold">GhostFlow</span>
            <span className="rounded bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide border border-blue-500/20">Beta</span>
          </div>
        </Link>
        <div className="px-2 pb-2">
          <TeamSwitcher />
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleMainItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton asChild isActive={isActive(item.href)}>
                    <Link to={item.href} onClick={handleClick}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {isAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel>
              <Shield className="mr-1 h-3 w-3" />
              Admin
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminNavItems.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton asChild isActive={isActive(item.href)}>
                      <Link to={item.href} onClick={handleClick}>
                        <item.icon className="h-4 w-4" />
                        <span className="flex-1">{item.title}</span>
                        {item.href === '/admin/tickets' && openTicketCount > 0 && (
                          <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">
                            {openTicketCount > 99 ? '99+' : openTicketCount}
                          </span>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t">
        <SidebarMenu>
          {visibleBottomItems.map((item) => (
            <SidebarMenuItem key={item.href}>
              <SidebarMenuButton asChild isActive={isActive(item.href)}>
                {'external' in item && item.external ? (
                  <a href={item.href} target="_blank" rel="noopener noreferrer">
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </a>
                ) : (
                  <Link to={item.href} onClick={handleClick}>
                    <item.icon className="h-4 w-4" />
                    <span className="flex-1">{item.title}</span>
                    {item.href === '/support' && openTicketCount > 0 && (
                      <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">
                        {openTicketCount > 99 ? '99+' : openTicketCount}
                      </span>
                    )}
                    {item.href === '/billing' && balance && (
                      <span className="ml-auto text-xs font-medium text-green-500">
                        {formatBalance(balance.balanceCents)}
                      </span>
                    )}
                  </Link>
                )}
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
